import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "./form-field";
import { Button } from "../ui/button";
import { useState, useEffect, useRef } from "react";
import { Input } from "../ui/input";
import EnhancedDynamicFieldsEditor from "./enhanced-dynamic-fields-editor";
import { useDeleteFunctionalField } from "@/service-hooks/use-delete-functional-field-service";
import {
  useUploadFileWithOptions,
  useSignedUrl,
} from "@/service-hooks/use-upload-file-service";
import { toast } from "sonner";
import { IMAGE_MIME_TYPES } from "@/lib/supported-file-types";
import { Image, X, Eye, ChevronDown, ChevronUp, Search, Check } from "lucide-react";
import { useGetNodesV2Service } from "@/service-hooks/use-get-nodes-v2-service";
import { StepItem } from "@/service-hooks/use-get-functional-graph-service";
import { Pagination } from "./pagination";
import useDebounce from "@/hooks/use-debounce";

interface FunctionalNodeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: any,
    deletedKeys?: string[],
    onSuccess?: () => void,
    onError?: (error: any) => void
  ) => void;
  node: any;
  label: string;
  projectUuid?: string;
  isReadOnly?: boolean;
}

/**
 * Convert a dynamic-field input (always text) back to the value sent to the API.
 * Booleans go out as booleans (BREEZEAI-1373: `isDesignGenerated` was sent as "true").
 * Numbers stay numbers only when the stored value was a number, so numeric-looking
 * text is not retyped. Arrays/objects keep the JSON-string form: Neo4j cannot
 * store nested maps as properties.
 */
function toPayloadValue(value: string, original: unknown): unknown {
  let parsedValue: unknown;
  try {
    parsedValue = JSON.parse(value);
  } catch {
    return value;
  }
  if (typeof parsedValue === "boolean") return parsedValue;
  if (typeof parsedValue === "number" && typeof original === "number") return parsedValue;
  return JSON.stringify(parsedValue);
}

export function FunctionalNodeDialog({
  isOpen,
  onClose,
  onSubmit,
  node,
  label,
  projectUuid,
  isReadOnly = false,
}: FunctionalNodeDialogProps) {
  // Reset all image state and Step dropdown states when dialog closes
  const handleDialogClose = () => {
    setUploadedImages([]);
    setUploadedImageUrls([]);
    // Reset Step-specific states
    setPreStepsOpen(false);
    setPostStepsOpen(false);
    setPreStepsSearch("");
    setPostStepsSearch("");
    setPreStepsPage(1);
    setPostStepsPage(1);
    setStepFields({
      preSteps: [],
      postSteps: [],
    });
    setSelectedStepsData({
      preSteps: [],
      postSteps: [],
    });
    onClose();
  };
  const [data, setData] = useState("");
  const [description, setDescription] = useState("");

  const deleteFieldMutation = useDeleteFunctionalField();

  // State for dynamic key-value pairs
  const [dynamicFields, setDynamicFields] = useState<
    Array<{ key: string; value: string }>
  >([{ key: "", value: "" }]);

  // Track original fields
  const [originalFields, setOriginalFields] = useState<Set<string>>(new Set());

  // State for Api-specific fields
  const [apiFields, setApiFields] = useState({
    url: "",
    method: "",
    type: "",
    request: "",
    response: "",
  });

  // Step-specific state
  const [stepFields, setStepFields] = useState({
    preSteps: [] as string[],
    postSteps: [] as string[],
  });

  // Store selected step objects for display (so we can show steps even when they're not on current page)
  const [selectedStepsData, setSelectedStepsData] = useState({
    preSteps: [] as StepItem[],
    postSteps: [] as StepItem[],
  });

  // Step dropdown states
  const [preStepsOpen, setPreStepsOpen] = useState(false);
  const [postStepsOpen, setPostStepsOpen] = useState(false);
  const [preStepsSearch, setPreStepsSearch] = useState("");
  const [postStepsSearch, setPostStepsSearch] = useState("");
  const [preStepsPage, setPreStepsPage] = useState(1);
  const [postStepsPage, setPostStepsPage] = useState(1);

  const debouncedPreStepsSearch = useDebounce(preStepsSearch, 300);
  const debouncedPostStepsSearch = useDebounce(postStepsSearch, 300);

  // API calls for Steps data - Load fresh data whenever dialog is open for Step label
  const { data: preStepsData, isLoading: isLoadingPreSteps } = useGetNodesV2Service({
    projectUuid: projectUuid || "",
    nodeType: "Step",
    limit: 10,
    page: preStepsPage,
    filters: debouncedPreStepsSearch ? { step: { '$containsi': debouncedPreStepsSearch } } : undefined,
    enabled: !!projectUuid && label === "Step" && isOpen,
  });

  const { data: postStepsData, isLoading: isLoadingPostSteps } = useGetNodesV2Service({
    projectUuid: projectUuid || "",
    nodeType: "Step",
    limit: 10,
    page: postStepsPage,
    filters: debouncedPostStepsSearch ? { step: { '$containsi': debouncedPostStepsSearch } } : undefined,
    enabled: !!projectUuid && label === "Step" && isOpen,
  });

  // API call to fetch details for selected steps (when editing existing node)
  const hasSelectedStepIds = stepFields.preSteps.length > 0 || stepFields.postSteps.length > 0;

  const { data: selectedStepsDetailsData } = useGetNodesV2Service({
    projectUuid: projectUuid || "",
    nodeType: "Step",
    limit: 50, // High limit to get all selected steps
    page: 1,
    enabled: !!projectUuid && label === "Step" && isOpen && hasSelectedStepIds && node?.id,
  });

  // Helper functions for step data
  const getAvailableSteps = (data: any) => {
    const steps = (data?.data as StepItem[]) || [];
    return steps.filter(step => step.id !== node?.id); // Exclude current step
  };

  const preStepsAvailable = getAvailableSteps(preStepsData);
  const postStepsAvailable = getAvailableSteps(postStepsData);

  // Step selection handlers
  const handleStepToggle = (stepId: string, type: 'preSteps' | 'postSteps') => {
    const availableSteps = type === 'preSteps' ? preStepsAvailable : postStepsAvailable;
    const stepObject = availableSteps.find(step => step.id === stepId);

    setStepFields(prev => {
      const currentIds = prev[type];
      if (currentIds.includes(stepId)) {
        return { ...prev, [type]: currentIds.filter(id => id !== stepId) };
      } else {
        return { ...prev, [type]: [...currentIds, stepId] };
      }
    });

    // Also update the selected steps data for display
    setSelectedStepsData(prev => {
      const currentSteps = prev[type];
      if (stepFields[type].includes(stepId)) {
        // Remove step
        return { ...prev, [type]: currentSteps.filter(step => step.id !== stepId) };
      } else {
        // Add step
        if (stepObject && !currentSteps.find(step => step.id === stepId)) {
          return { ...prev, [type]: [...currentSteps, stepObject] };
        }
        return prev;
      }
    });
  };

  const handleRemoveStep = (stepId: string, type: 'preSteps' | 'postSteps') => {
    setStepFields(prev => ({
      ...prev,
      [type]: prev[type].filter(id => id !== stepId)
    }));

    setSelectedStepsData(prev => ({
      ...prev,
      [type]: prev[type].filter(step => step.id !== stepId)
    }));
  };

  const handleClearAll = (type: 'preSteps' | 'postSteps') => {
    setStepFields(prev => ({ ...prev, [type]: [] }));
    setSelectedStepsData(prev => ({ ...prev, [type]: [] }));
  };

  // Pagination handlers
  const handlePreStepsNextPage = () => setPreStepsPage(prev => Math.min(preStepsData?.totalPages || 1, prev + 1));
  const handlePreStepsPrevPage = () => setPreStepsPage(prev => Math.max(1, prev - 1));
  const handlePostStepsNextPage = () => setPostStepsPage(prev => Math.min(postStepsData?.totalPages || 1, prev + 1));
  const handlePostStepsPrevPage = () => setPostStepsPage(prev => Math.max(1, prev - 1));

  // Reset search and page when Step dialog opens (not just dropdowns)
  useEffect(() => {
    if (isOpen && label === "Step") {
      setPreStepsSearch("");
      setPostStepsSearch("");
      setPreStepsPage(1);
      setPostStepsPage(1);
      // Always ensure both dropdowns are ready to show fresh data
      setPreStepsOpen(false);
      setPostStepsOpen(false);
      // Reset step fields to trigger fresh data binding
      setStepFields({
        preSteps: [],
        postSteps: [],
      });
      setSelectedStepsData({
        preSteps: [],
        postSteps: [],
      });
    }
  }, [isOpen, label]);

  // Reset search when dropdown opens (but not page - let users keep their pagination)
  useEffect(() => {
    if (preStepsOpen) {
      setPreStepsSearch("");
    }
  }, [preStepsOpen]);

  useEffect(() => {
    if (postStepsOpen) {
      setPostStepsSearch("");
    }
  }, [postStepsOpen]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setPreStepsPage(1);
  }, [debouncedPreStepsSearch]);

  useEffect(() => {
    setPostStepsPage(1);
  }, [debouncedPostStepsSearch]);

  // Populate selectedStepsData with fetched step details when editing existing node
  useEffect(() => {
    if (selectedStepsDetailsData?.data && node?.id && (stepFields.preSteps.length > 0 || stepFields.postSteps.length > 0)) {
      const allStepsDetails = selectedStepsDetailsData.data as StepItem[];

      const preStepsDetails = allStepsDetails.filter(step => stepFields.preSteps.includes(step.id));
      const postStepsDetails = allStepsDetails.filter(step => stepFields.postSteps.includes(step.id));

      setSelectedStepsData({
        preSteps: preStepsDetails,
        postSteps: postStepsDetails,
      });
    }
  }, [selectedStepsDetailsData, stepFields.preSteps, stepFields.postSteps, node?.id]);

  // State for image upload functionality
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [uploadedImageUrls, setUploadedImageUrls] = useState<string[]>([]);

  // State for image preview modal
  const [previewImage, setPreviewImage] = useState<{
    url: string;
    name: string;
    index: number;
    needsSignedUrl?: boolean;
  } | null>(null);

  // Get signed URL for preview image when needed
  const hasValidPreviewUrl = Boolean(
    previewImage?.url &&
      previewImage.url.trim() &&
      previewImage.url !== "undefined" &&
      previewImage.needsSignedUrl
  );

  const {
    isLoading: isLoadingSignedUrl,
    data: signedPreviewUrl,
    refetch: refetchSignedUrl,
  } = useSignedUrl(previewImage?.url || "", hasValidPreviewUrl);

  // State for display URL management
  const [displaySignedUrl, setDisplaySignedUrl] = useState<string | null>(null);

  // Update display URL when signedUrl is ready
  useEffect(() => {
    if (signedPreviewUrl && previewImage?.needsSignedUrl) {
      setDisplaySignedUrl(signedPreviewUrl);
    } else if (!previewImage?.needsSignedUrl && previewImage?.url) {
      setDisplaySignedUrl(previewImage.url);
    } else {
      setDisplaySignedUrl(null);
    }
  }, [signedPreviewUrl, previewImage]);

  // Clear display URL when preview modal closes
  useEffect(() => {
    if (!previewImage) {
      setDisplaySignedUrl(null);
    }
  }, [previewImage]);

  // Upload mutation for images
  const uploadMutation = useUploadFileWithOptions((_, error) => {
    if (error) {
      toast.error(`Upload failed: ${error.message}`);
    }
  });

  // Image upload handlers
  const handleImageFileUpload = async (files: FileList) => {
    const filesArray = Array.from(files);

    const validFiles = filesArray.filter((file) =>
      IMAGE_MIME_TYPES.includes(file.type.toLowerCase())
    );

    if (validFiles.length !== filesArray.length) {
      toast.error("Only image files (PNG, JPEG, JPG) are allowed");
      return;
    }

    // Update state immediately
    setUploadedImages((prev) => [...prev, ...validFiles]);

    // Upload files to server
    try {
      const uploadPromises = validFiles.map(async (file) => {
        const uploadResult = await uploadMutation.mutateAsync({ file });
        return uploadResult.fileUrl || uploadResult.url || uploadResult.s3Url;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      setUploadedImageUrls((prev) => [...prev, ...uploadedUrls]);

      toast.success(
        `${validFiles.length} ${
          validFiles.length === 1 ? "image" : "images"
        } uploaded successfully`
      );
    } catch (error) {
      toast.error(`Upload failed: ${error}`);
    }
  };

  const handleRemoveImage = (filename: string) => {
    setUploadedImages((prev) => {
      const fileIndex = prev.findIndex((file) => file.name === filename);
      if (fileIndex !== -1) {
        // Also remove the corresponding URL at the same index
        setUploadedImageUrls((urlPrev) =>
          urlPrev.filter((_, index) => index !== fileIndex)
        );
        return prev.filter((file) => file.name !== filename);
      }
      return prev;
    });
  };

  const handleImageClick = (file: File, index: number) => {
    const isExisting = (file as any).isExisting;
    const imageUrl = isExisting
      ? (file as any).url
      : uploadedImageUrls[index] || URL.createObjectURL(file);

    // Check if this is an AWS S3 URL that needs signing
    const needsSignedUrl =
      isExisting && imageUrl && imageUrl.includes("amazonaws.com");

    setPreviewImage({
      url: imageUrl,
      name: file.name,
      index,
      needsSignedUrl,
    });

    // Refetch signed URL when needed
    if (needsSignedUrl && imageUrl) {
      setTimeout(() => refetchSignedUrl(), 100);
    }
  };

  const handleClosePreview = () => {
    setPreviewImage(null);
  };

  useEffect(() => {
    // Always reset image state first when node or label changes
    setUploadedImages([]);
    setUploadedImageUrls([]);

    if (node) {
      if (label === "Api") {
        // Handle Api-specific fields
        setApiFields({
          url: node.url || "",
          method: node.method || "",
          type: node.type || "",
          request: node.request || "",
          response: node.response || "",
        });

        // Initialize dynamic fields for API endpoints (exclude api-specific and system fields)
        const dynamicData: Array<{ key: string; value: string }> = [];

        if (typeof node === "object" && node !== null) {
          Object.entries(node).forEach(([key, value]) => {
            // Skip api-specific fields and system fields.
            // `type` belongs to apiFields — leaving it out lets a stale copy
            // ride along in dynamicFields and overwrite the edited value at
            // save time.
            const systemFields = [
              "id",
              "metadata",
              "functionalId",
              "createdAt",
              "embedding",
              "projectUuid",
              "updatedAt",
              "citations",
              "personaId",
              "outcomeId",
              "description",
              "scenarioId",
              "stepId",
              "actionId",
              "url",
              "method",
              "type",
              "request",
              "response",
              "screenshot",
            ];
            const shouldSkip =
              systemFields.includes(key) ||
              systemFields.includes(key.toLowerCase());

            if (!shouldSkip) {
              dynamicData.push({
                key,
                value:
                  typeof value === "string" ? value : JSON.stringify(value),
              });
            }
          });
        }

        // Always ensure there's at least one empty row for adding new properties
        if (
          dynamicData.length === 0 ||
          dynamicData.every((field) => field.key.trim() !== "")
        ) {
          dynamicData.push({ key: "", value: "" });
        }

        setDynamicFields(dynamicData);

        // Track original field keys
        const originalKeys = new Set(
          dynamicData
            .filter((field) => field.key.trim())
            .map((field) => field.key)
        );
        setOriginalFields(originalKeys);
      } else {
        // Handle other labels as before
        const mainFieldKey = label.toLowerCase();
        setData(node[mainFieldKey] || "");
        setDescription(node.description || "");

        // Initialize Step-specific fields
        if (label === "Step") {
          setStepFields({
            preSteps: Array.isArray(node.preSteps) ? node.preSteps : [],
            postSteps: Array.isArray(node.postSteps) ? node.postSteps : [],
          });
        }

        // Initialize dynamic fields from node properties (exclude main field and system fields)
        const dynamicData: Array<{ key: string; value: string }> = [];

        if (typeof node === "object" && node !== null) {
          Object.entries(node).forEach(([key, value]) => {
            // Skip the main field and system fields
            const systemFields = [
              "id",
              "metadata",
              "functionalId",
              "createdAt",
              "embedding",
              "projectUuid",
              "updatedAt",
              "citations",
              "personaId",
              "outcomeId",
              "description",
              "scenarioId",
              "stepId",
              "actionId",
              "url",
              "method",
              "request",
              "response",
              "screenshot",
              "preSteps",
              "postSteps",
            ];
            const shouldSkip =
              systemFields.includes(key) ||
              systemFields.includes(key.toLowerCase()) ||
              key === mainFieldKey;

            if (!shouldSkip) {
              dynamicData.push({
                key,
                value:
                  typeof value === "string" ? value : JSON.stringify(value),
              });
            }
          });
        }

        // Always ensure there's at least one empty row for adding new properties
        if (
          dynamicData.length === 0 ||
          dynamicData.every((field) => field.key.trim() !== "")
        ) {
          dynamicData.push({ key: "", value: "" });
        }

        setDynamicFields(dynamicData);

        // Track original field keys
        const originalKeys = new Set(
          dynamicData
            .filter((field) => field.key.trim())
            .map((field) => field.key)
        );
        setOriginalFields(originalKeys);

        // Load existing screenshot data for Actions
        if (label === "Action" && node.screenshot) {
          try {
            const existingScreenshots = JSON.parse(node.screenshot);

            if (Array.isArray(existingScreenshots)) {
              setUploadedImageUrls(existingScreenshots);
              // Create mock File objects for display purposes
              const mockFiles = existingScreenshots.map((url, index) => {
                const rawName = url.split("/").pop()?.split("?")[0];
                let filename = rawName || `screenshot_${index + 1}.jpg`;
                try {
                  if (rawName) filename = decodeURIComponent(rawName);
                } catch {
                  /* keep raw if not valid %-encoding */
                }
                const mockFile = new File([""], filename, {
                  type: "image/jpeg",
                });
                // Add a property to identify this as an existing image
                (mockFile as any).isExisting = true;
                (mockFile as any).url = url;
                return mockFile;
              });
              setUploadedImages(mockFiles);
            }
          } catch (error) {
            console.error("Error parsing screenshot data:", error);
          }
        } else if (label === "Action") {
          setUploadedImages([]);
          setUploadedImageUrls([]);
        }
      }
    } else {
      if (label === "Api") {
        setApiFields({
          url: "",
          method: "",
          type: "",
          request: "",
          response: "",
        });
        setDynamicFields([{ key: "", value: "" }]);
        setOriginalFields(new Set());
      } else {
        setData("");
        setDescription("");
        setDynamicFields([{ key: "", value: "" }]);
        setOriginalFields(new Set());
      }
    }
  }, [node, label]);

  const handleSubmit = async () => {
    let result: Record<string, any> = {};

    if (label === "Api") {
      // Handle Api-specific fields
      result = {
        url: apiFields.url,
        method: apiFields.method,
        type: apiFields.type,
        request: apiFields.request,
        response: apiFields.response,
      };

      // Add dynamic fields to the result for API endpoints
      dynamicFields.forEach(({ key, value }) => {
        if (key.trim()) {
          result[key] = toPayloadValue(value, node?.[key]);
        }
      });
    } else {
      // Handle other labels as before
      const mainFieldKey = label.toLowerCase();
      // Reject empty/whitespace-only names and normalize internal whitespace so
      // "  Foo   Bar  " becomes "Foo Bar" — stops accidental blank/space-only
      // nodes from being created (previously silently accepted).
      const normalizedName = data.replace(/\s+/g, " ").trim();
      if (!normalizedName) {
        toast.error(`${label} name cannot be empty`);
        return;
      }
      // Name must START with a letter or digit. Special characters (hyphen,
      // underscore, quotes, etc.) are allowed inside the name — this keeps
      // legitimate names like "Test-EndUser" working while still blocking
      // leading punctuation that breaks alphabetical sort / search.
      if (!/^[A-Za-z0-9]/.test(normalizedName)) {
        toast.error(
          `${label} name must start with a letter or number`,
        );
        return;
      }
      result = {
        [mainFieldKey]: normalizedName,
        description: description,
      };

      // Add dynamic fields to the result
      dynamicFields.forEach(({ key, value }) => {
        if (key.trim() && key !== mainFieldKey) {
          if (
            key === "order" &&
            (label === "Step" || label === "Action")
          ) {
            const numericOrder = Number(value);
            result[key] = Number.isFinite(numericOrder) ? numericOrder : value;
            return;
          }
          // Never send citationRefs (or a citationIds equivalent) in the update
          // payload. The existing citationRefs stays untouched in the backend
          // since it isn't included here.
          if (key === "citationRefs") {
            return;
          }
          result[key] = toPayloadValue(value, node?.[key]);
        }
      });

      // Add screenshot URLs for Action labels
      if (label === "Action") {
        if (uploadedImageUrls.length > 0) {
          result.screenshot = JSON.stringify(uploadedImageUrls);
        } else {
          // Ensure we clear screenshot field if no images
          result.screenshot = JSON.stringify([]);
        }
      }

      // Add Step-specific fields
      if (label === "Step") {
        result.preSteps = stepFields.preSteps;
        result.postSteps = stepFields.postSteps;
      }
    }

    // Check what fields were deleted (for both Api and other labels)
    let keysToDelete: string[] = [];
    const currentFieldKeys = new Set(
      dynamicFields
        .filter((field) => field.key.trim())
        .map((field) => field.key)
    );
    keysToDelete = Array.from(originalFields).filter(
      (key) => !currentFieldKeys.has(key)
    );

    // If there are fields to delete and this is an existing node, call delete API first
    if (keysToDelete.length > 0 && node && node.id) {
      try {
        await deleteFieldMutation.mutateAsync({
          label: label,
          nodeId: node.id,
          keys: keysToDelete,
        });
        // toast.success(`Successfully deleted ${keysToDelete.length} field${keysToDelete.length > 1 ? 's' : ''}: ${keysToDelete.join(', ')}`);
      } catch (error) {
        console.error("Failed to delete fields:", error);
        toast.error(
          `Failed to delete fields: ${keysToDelete.join(", ")}. ${
            (error as any)?.message || "Unknown error"
          }`
        );
        // Continue with update even if delete fails
      }
    }

    // Define success and error handlers for the update operation
    const onUpdateSuccess = () => {
      let hasChanges = false;

      if (label === "Api") {
        // Check for changes in Api fields
        hasChanges = Object.keys(apiFields).some((key) => {
          return (
            apiFields[key as keyof typeof apiFields] !== (node?.[key] || "")
          );
        });

        // Also check for changes in dynamic fields
        if (!hasChanges) {
          hasChanges =
            keysToDelete.length > 0 ||
            Object.keys(result).some((key) => {
              // Skip the main api fields since we already checked them
              if (["url", "method", "request", "response"].includes(key)) {
                return false;
              }
              return !node || node[key] !== result[key];
            });
        }
      } else {
        hasChanges =
          keysToDelete.length > 0 ||
          Object.keys(result).some((key) => {
            if (key === label.toLowerCase()) {
              return data !== (node?.[key] || "");
            }
            return !node || node[key] !== result[key];
          });
      }

      if (hasChanges || !node) {
        if (node) {
          toast.success(`${label} updated successfully!`);
        } else {
          toast.success(`${label} created successfully!`);
        }
      }

      // For Actions with screenshot changes, add extra delay to ensure server processing
      if (
        label === "Action" &&
        (uploadedImageUrls.length > 0 || (node && node.screenshot))
      ) {
        setTimeout(() => {
          handleDialogClose();
        }, 1000); // Longer delay for screenshot updates
      } else {
        setTimeout(() => {
          handleDialogClose();
        }, 300);
      }
    };

    const onUpdateError = (error: any) => {
      const operation = node ? "update" : "create";
      toast.error(
        `Failed to ${operation} ${label.toLowerCase()}: ${
          (error as any)?.message || "Unknown error"
        }`
      );
    };

    onSubmit(result, keysToDelete, onUpdateSuccess, onUpdateError);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleDialogClose()}>
      <DialogContent className="sm:max-w-md bg-surface text-white border-surface-border max-h-[80vh] flex flex-col">
        {/* Fixed Header */}
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl font-semibold text-white">
            {isReadOnly ? `View ${label}` : node ? `Edit ${label}` : `Create ${label}`}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isReadOnly
              ? `View details for this ${label.toLowerCase()}.`
              : node
                ? `Edit fields for this ${label.toLowerCase()} and save your changes.`
                : `Fill in the fields to create a new ${label.toLowerCase()}.`}
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto py-3 px-1">
          <div className="grid gap-3">
            {label === "Api" ? (
              <>
                <FormField label="URL" required>
                  <Input
                    value={apiFields.url}
                    onChange={(e) =>
                      setApiFields((prev) => ({ ...prev, url: e.target.value }))
                    }
                    className="bg-surface-active border-surface-border text-white placeholder:text-muted"
                    placeholder="Enter API endpoint URL"
                    required
                    readOnly={isReadOnly}
                  />
                </FormField>

                <FormField label="Type">
                  {/* Free-text input with a styled suggestion dropdown. Backend
                      stores apis[].type as free text (see api-graph.entity.ts),
                      so the UI mirrors that — pick a known protocol or type a
                      new one (e.g. SOAP); values outside the preset list still
                      render and survive an edit/save round-trip. Native
                      <datalist> was unreliable across browsers and themed badly
                      against the dark dialog, so this is a small inline combo. */}
                  <ApiTypeCombobox
                    value={apiFields.type}
                    onChange={(next) =>
                      setApiFields((prev) =>
                        prev.type === next
                          ? prev
                          : { ...prev, type: next, method: "" }
                      )
                    }
                    readOnly={isReadOnly}
                  />
                </FormField>

                <FormField label="Method" required>
                  {apiFields.type === "REST" ||
                  apiFields.type === "GraphQL" ||
                  apiFields.type === "gRPC" ||
                  apiFields.type === "WebSocket" ||
                  apiFields.type === "Event" ? (
                    <select
                      value={apiFields.method}
                      onChange={(e) =>
                        setApiFields((prev) => ({
                          ...prev,
                          method: e.target.value,
                        }))
                      }
                      className="w-full bg-surface-active border border-surface-border rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-70"
                      required
                      disabled={isReadOnly}
                    >
                      {apiFields.type === "REST" ? (
                        <>
                          <option value="">Select HTTP Method</option>
                          <option value="GET">GET</option>
                          <option value="POST">POST</option>
                          <option value="PUT">PUT</option>
                          <option value="PATCH">PATCH</option>
                          <option value="DELETE">DELETE</option>
                        </>
                      ) : apiFields.type === "GraphQL" ? (
                        <>
                          <option value="">Select Operation</option>
                          <option value="QUERY">QUERY</option>
                          <option value="MUTATION">MUTATION</option>
                          <option value="SUBSCRIPTION">SUBSCRIPTION</option>
                        </>
                      ) : apiFields.type === "gRPC" ? (
                        <>
                          <option value="">Select Stream Type</option>
                          <option value="UNARY">UNARY</option>
                          <option value="SERVER_STREAM">SERVER_STREAM</option>
                          <option value="CLIENT_STREAM">CLIENT_STREAM</option>
                          <option value="BIDI_STREAM">BIDI_STREAM</option>
                        </>
                      ) : apiFields.type === "WebSocket" ? (
                        <>
                          <option value="">Select Direction</option>
                          <option value="EMIT">EMIT</option>
                          <option value="ON">ON</option>
                          <option value="SUBSCRIBE">SUBSCRIBE</option>
                        </>
                      ) : (
                        <>
                          <option value="">Select Type</option>
                          <option value="PUBLISH">PUBLISH</option>
                          <option value="CONSUME">CONSUME</option>
                        </>
                      )}
                    </select>
                  ) : (
                    // SOAP / custom protocols don't have a fixed method set —
                    // accept any value (e.g. SOAP operation name, "POST", etc.).
                    <Input
                      value={apiFields.method}
                      onChange={(e) =>
                        setApiFields((prev) => ({
                          ...prev,
                          method: e.target.value,
                        }))
                      }
                      placeholder="e.g. operation name, HTTP verb"
                      className="bg-surface-active border-surface-border text-white placeholder:text-muted"
                      required
                      readOnly={isReadOnly}
                    />
                  )}
                </FormField>

                <FormField label="Request">
                  <textarea
                    value={apiFields.request}
                    onChange={(e) =>
                      setApiFields((prev) => ({
                        ...prev,
                        request: e.target.value,
                      }))
                    }
                    className="w-full bg-surface-active border border-surface-border rounded-md px-3 py-2 text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical min-h-[100px]"
                    placeholder="Enter request payload (JSON format)"
                    readOnly={isReadOnly}
                  />
                </FormField>

                <FormField label="Response">
                  <textarea
                    value={apiFields.response}
                    onChange={(e) =>
                      setApiFields((prev) => ({
                        ...prev,
                        response: e.target.value,
                      }))
                    }
                    className="w-full bg-surface-active border border-surface-border rounded-md px-3 py-2 text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical min-h-[100px]"
                    placeholder="Enter expected response"
                    readOnly={isReadOnly}
                  />
                </FormField>

                <EnhancedDynamicFieldsEditor
                  dynamicFields={dynamicFields}
                  setDynamicFields={setDynamicFields}
                  isReadOnly={isReadOnly}
                />
              </>
            ) : (
              <>
                <FormField label={label} required>
                  <Input
                    value={data}
                    onChange={(e) => setData(e.target.value)}
                    className="bg-surface-active text-white placeholder:text-muted"
                    required
                    readOnly={isReadOnly}
                  />
                </FormField>

                {/* Description field - Show for all non-Api labels */}
                <FormField label="Description">
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full bg-surface-active border border-surface-border rounded-md px-3 py-2 text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical min-h-[100px]"
                    placeholder={`Enter description for this ${label.toLowerCase()}`}
                    readOnly={isReadOnly}
                  />
                </FormField>

                {/* Step-specific fields - Only show for Step (hidden in view mode) */}
                {label === "Step" && projectUuid && !isReadOnly && (
                  <>
                    
                    {/* Pre-requisite Steps Dropdown */}
                    <FormField label="Pre Steps">
                      <div className="space-y-2">
                        {/* Selected PreSteps Display */}
                        {stepFields.preSteps.length > 0 && (
                          <div className="flex flex-wrap gap-2 p-2 bg-surface-active border border-surface-border rounded">
                            {selectedStepsData.preSteps.map(step => (
                              <div
                                key={step.id}
                                className="flex items-center gap-1 bg-primary/20 text-primary px-2 py-1 rounded-sm text-sm"
                              >
                                <span className="truncate max-w-[200px]">{step.step}</span>
                                <button
                                  onClick={() => handleRemoveStep(step.id, 'preSteps')}
                                  className="hover:bg-primary/30 rounded-full p-0.5"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                            {/* Show chips for IDs that don't have step objects yet - with names from selectedStepsDetailsData if available */}
                            {stepFields.preSteps
                              .filter(id => !selectedStepsData.preSteps.find(step => step.id === id))
                              .map(stepId => {
                                // Try to find step name from selectedStepsDetailsData
                                const stepDetail = selectedStepsDetailsData?.data?.find((step: StepItem) => step.id === stepId);
                                const displayName = stepDetail ? stepDetail.step : `Loading step ${stepId.slice(-8)}...`;

                                return (
                                  <div
                                    key={stepId}
                                    className="flex items-center gap-1 bg-primary/20 text-primary px-2 py-1 rounded-sm text-sm"
                                  >
                                    <span className="truncate max-w-[200px]">{displayName}</span>
                                    <button
                                      onClick={() => handleRemoveStep(stepId, 'preSteps')}
                                      className="hover:bg-primary/30 rounded-full p-0.5"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                );
                              })
                            }
                            <button
                              onClick={() => handleClearAll('preSteps')}
                              className="text-muted hover:text-white text-xs px-2 py-1 rounded border border-surface-border hover:border-primary"
                            >
                              Clear All
                            </button>
                          </div>
                        )}

                        {/* Dropdown Trigger */}
                        <div
                          onClick={() => setPreStepsOpen(!preStepsOpen)}
                          className="flex items-center justify-between w-full p-2 bg-surface-active border border-surface-border rounded cursor-pointer hover:border-primary transition-colors"
                        >
                          <span className="text-white truncate">
                            {stepFields.preSteps.length > 0
                              ? `${stepFields.preSteps.length} step${stepFields.preSteps.length > 1 ? 's' : ''} selected`
                              : "Select steps"
                            }
                          </span>
                          {preStepsOpen ? (
                            <ChevronUp className="h-4 w-4 text-muted" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted" />
                          )}
                        </div>

                        {/* Dropdown Content */}
                        {preStepsOpen && (
                          <div className="relative z-50 w-full mt-1 bg-surface border border-surface-border rounded-lg shadow-lg">
                            <div className="p-3 border-b border-surface-border">
                              <div className="relative">
                                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted" />
                                <Input
                                  value={preStepsSearch}
                                  onChange={(e) => setPreStepsSearch(e.target.value)}
                                  placeholder="Search steps..."
                                  className="pl-8 bg-surface-active border-surface-border text-white placeholder:text-muted"
                                />
                              </div>
                            </div>

                            <div className="max-h-60 overflow-y-auto">
                              {isLoadingPreSteps ? (
                                <div className="p-4 text-center text-muted">
                                  <div className="flex items-center justify-center gap-2">
                                    <div className="w-4 h-4 border-2 border-muted border-t-transparent rounded-full animate-spin"></div>
                                    Loading steps...
                                  </div>
                                </div>
                              ) : preStepsAvailable.length === 0 ? (
                                <div className="p-4 text-center text-muted">
                                  {debouncedPreStepsSearch ? "No steps found matching your search." : "No steps available."}
                                </div>
                              ) : (
                                <div className="py-2">
                                  {preStepsAvailable.map((step) => (
                                    <div
                                      key={step.id}
                                      onClick={() => handleStepToggle(step.id, 'preSteps')}
                                      className="flex items-center gap-3 px-3 py-2 hover:bg-surface-hover cursor-pointer transition-colors"
                                    >
                                      <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                                        stepFields.preSteps.includes(step.id)
                                          ? 'bg-primary border-primary'
                                          : 'border-surface-border'
                                      }`}>
                                        {stepFields.preSteps.includes(step.id) && (
                                          <Check className="h-3 w-3 text-white" />
                                        )}
                                      </div>
                                      <span className="text-white flex-1 truncate">{step.step}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Pagination */}
                            {(preStepsData?.totalPages || 0) > 1 && (
                              <div className="p-3 border-t border-surface-border">
                                <Pagination
                                  currentItems={preStepsAvailable.length}
                                  totalItems={preStepsData?.total || 0}
                                  currentPage={preStepsPage}
                                  totalPages={preStepsData?.totalPages || 1}
                                  isLoading={isLoadingPreSteps}
                                  onPrevious={handlePreStepsPrevPage}
                                  onNext={handlePreStepsNextPage}
                                  canGoPrevious={preStepsPage > 1}
                                  canGoNext={preStepsPage < (preStepsData?.totalPages || 1)}
                                  itemName="steps"
                                  showPageNumbers={true}
                                  mode="pages"
                                  className="bg-surface-active"
                                />
                              </div>
                            )}

                            <div className="p-3 border-t border-surface-border flex justify-end">
                              <Button
                                onClick={() => setPreStepsOpen(false)}
                                variant="outline"
                                size="sm"
                                className="bg-surface-hover text-white hover:bg-white border-surface-border"
                              >
                                Close
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </FormField>

                    {/* Follow-up Steps Dropdown */}
                    <FormField label="Post Steps">
                      <div className="space-y-2">
                        {/* Selected PostSteps Display */}
                        {stepFields.postSteps.length > 0 && (
                          <div className="flex flex-wrap gap-2 p-2 bg-surface-active border border-surface-border rounded">
                            {selectedStepsData.postSteps.map(step => (
                              <div
                                key={step.id}
                                className="flex items-center gap-1 bg-primary/20 text-primary px-2 py-1 rounded-sm text-sm"
                              >
                                <span className="truncate max-w-[200px]">{step.step}</span>
                                <button
                                  onClick={() => handleRemoveStep(step.id, 'postSteps')}
                                  className="hover:bg-primary/30 rounded-full p-0.5"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                            {/* Show chips for IDs that don't have step objects yet - with names from selectedStepsDetailsData if available */}
                            {stepFields.postSteps
                              .filter(id => !selectedStepsData.postSteps.find(step => step.id === id))
                              .map(stepId => {
                                // Try to find step name from selectedStepsDetailsData
                                const stepDetail = selectedStepsDetailsData?.data?.find((step: StepItem) => step.id === stepId);
                                const displayName = stepDetail ? stepDetail.step : `Loading step ${stepId.slice(-8)}...`;

                                return (
                                  <div
                                    key={stepId}
                                    className="flex items-center gap-1 bg-primary/20 text-primary px-2 py-1 rounded-sm text-sm"
                                  >
                                    <span className="truncate max-w-[200px]">{displayName}</span>
                                    <button
                                      onClick={() => handleRemoveStep(stepId, 'postSteps')}
                                      className="hover:bg-primary/30 rounded-full p-0.5"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                );
                              })
                            }
                            <button
                              onClick={() => handleClearAll('postSteps')}
                              className="text-muted hover:text-white text-xs px-2 py-1 rounded border border-surface-border hover:border-primary"
                            >
                              Clear All
                            </button>
                          </div>
                        )}

                        {/* Dropdown Trigger */}
                        <div
                          onClick={() => setPostStepsOpen(!postStepsOpen)}
                          className="flex items-center justify-between w-full p-2 bg-surface-active border border-surface-border rounded cursor-pointer hover:border-primary transition-colors"
                        >
                          <span className="text-white truncate">
                            {stepFields.postSteps.length > 0
                              ? `${stepFields.postSteps.length} step${stepFields.postSteps.length > 1 ? 's' : ''} selected`
                              : "Select steps"
                            }
                          </span>
                          {postStepsOpen ? (
                            <ChevronUp className="h-4 w-4 text-muted" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted" />
                          )}
                        </div>

                        {/* Dropdown Content */}
                        {postStepsOpen && (
                          <div className="relative z-50 w-full mt-1 bg-surface border border-surface-border rounded-lg shadow-lg">
                            <div className="p-3 border-b border-surface-border">
                              <div className="relative">
                                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted" />
                                <Input
                                  value={postStepsSearch}
                                  onChange={(e) => setPostStepsSearch(e.target.value)}
                                  placeholder="Search steps..."
                                  className="pl-8 bg-surface-active border-surface-border text-white placeholder:text-muted"
                                />
                              </div>
                            </div>

                            <div className="max-h-60 overflow-y-auto">
                              {isLoadingPostSteps ? (
                                <div className="p-4 text-center text-muted">
                                  <div className="flex items-center justify-center gap-2">
                                    <div className="w-4 h-4 border-2 border-muted border-t-transparent rounded-full animate-spin"></div>
                                    Loading steps...
                                  </div>
                                </div>
                              ) : postStepsAvailable.length === 0 ? (
                                <div className="p-4 text-center text-muted">
                                  {debouncedPostStepsSearch ? "No steps found matching your search." : "No steps available."}
                                </div>
                              ) : (
                                <div className="py-2">
                                  {postStepsAvailable.map((step) => (
                                    <div
                                      key={step.id}
                                      onClick={() => handleStepToggle(step.id, 'postSteps')}
                                      className="flex items-center gap-3 px-3 py-2 hover:bg-surface-hover cursor-pointer transition-colors"
                                    >
                                      <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                                        stepFields.postSteps.includes(step.id)
                                          ? 'bg-primary border-primary'
                                          : 'border-surface-border'
                                      }`}>
                                        {stepFields.postSteps.includes(step.id) && (
                                          <Check className="h-3 w-3 text-white" />
                                        )}
                                      </div>
                                      <span className="text-white flex-1 truncate">{step.step}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Pagination */}
                            {(postStepsData?.totalPages || 0) > 1 && (
                              <div className="p-3 border-t border-surface-border">
                                <Pagination
                                  currentItems={postStepsAvailable.length}
                                  totalItems={postStepsData?.total || 0}
                                  currentPage={postStepsPage}
                                  totalPages={postStepsData?.totalPages || 1}
                                  isLoading={isLoadingPostSteps}
                                  onPrevious={handlePostStepsPrevPage}
                                  onNext={handlePostStepsNextPage}
                                  canGoPrevious={postStepsPage > 1}
                                  canGoNext={postStepsPage < (postStepsData?.totalPages || 1)}
                                  itemName="steps"
                                  showPageNumbers={true}
                                  mode="pages"
                                  className="bg-surface-active"
                                />
                              </div>
                            )}

                            <div className="p-3 border-t border-surface-border flex justify-end">
                              <Button
                                onClick={() => setPostStepsOpen(false)}
                                variant="outline"
                                size="sm"
                                className="bg-surface-hover text-white hover:bg-white border-surface-border"
                              >
                                Close
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </FormField>
                  </>
                )}

                {/* Upload Images Section - Only show for Action */}
                {label === "Action" &&
                  (!isReadOnly || uploadedImages.length > 0) && (
                  <div className="mb-3">
                    <label className="block text-white text-sm font-medium mb-1">
                      {isReadOnly ? "Images" : "Upload Images"}
                    </label>

                    {!isReadOnly && (
                      <div className="border-2 border-dashed border-surface-active rounded-lg p-3 text-center hover:border-primary transition-colors mb-2">
                        <input
                          type="file"
                          multiple
                          accept={IMAGE_MIME_TYPES.join(",")}
                          onChange={(e) =>
                            e.target.files &&
                            handleImageFileUpload(e.target.files)
                          }
                          className="hidden"
                          id="action-image-upload"
                        />
                        <label
                          htmlFor="action-image-upload"
                          className="cursor-pointer flex flex-col items-center gap-2"
                        >
                          <Image className="w-5 h-5 text-muted" />
                          <div>
                            <p className="text-white font-medium text-sm">
                              Click to upload images
                            </p>
                            <p className="text-muted text-xs">
                              PNG, JPEG, JPG up to 10MB each
                            </p>
                          </div>
                        </label>
                      </div>
                    )}
                    {uploadedImages.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-white text-sm font-medium">
                          Uploaded Images ({uploadedImages.length})
                        </div>

                        {/* Image Previews Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                          {uploadedImages.map((file, index) => {
                            const isExisting = (file as any).isExisting;
                            const imageUrl = isExisting
                              ? (file as any).url
                              : uploadedImageUrls[index] ||
                                URL.createObjectURL(file);

                            return (
                              <div
                                key={index}
                                className="relative group bg-surface-card rounded-lg border border-surface-border overflow-hidden"
                              >
                                {/* Image Preview */}
                                <div
                                  className="aspect-square w-full cursor-pointer group/image relative"
                                  onClick={() => handleImageClick(file, index)}
                                >
                                  <img
                                    src={imageUrl}
                                    alt={file.name}
                                    className="w-full h-full object-cover transition-transform group-hover/image:scale-105"
                                    onError={(e) => {
                                      // Fallback to file icon if image fails to load
                                      (
                                        e.target as HTMLImageElement
                                      ).style.display = "none";
                                      (
                                        e.target as HTMLImageElement
                                      ).nextElementSibling?.classList.remove(
                                        "hidden"
                                      );
                                    }}
                                  />
                                  {/* Fallback icon (hidden by default) */}
                                  <div className="hidden w-full h-full flex items-center justify-center bg-surface-active">
                                    <Image className="w-5 h-5 text-muted" />
                                  </div>

                                  {/* Hover overlay with eye icon */}
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/image:opacity-100 transition-opacity flex items-center justify-center">
                                    <Eye className="w-6 h-6 text-white" />
                                  </div>
                                </div>

                                {/* Image Info Overlay */}
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                                  <div className="text-white text-xs truncate font-medium">
                                    {file.name}
                                  </div>
                                  {!isExisting && (
                                    <div className="text-muted text-xs">
                                      {Math.round(file.size / 1024)} KB
                                    </div>
                                  )}
                                </div>

                                {/* Remove Button */}
                                {!isReadOnly && (
                                  <button
                                    onClick={() => handleRemoveImage(file.name)}
                                    className="absolute top-1 right-1 bg-error/80 hover:bg-error text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <EnhancedDynamicFieldsEditor
                  dynamicFields={dynamicFields}
                  setDynamicFields={setDynamicFields}
                  isReadOnly={isReadOnly}
                />
              </>
            )}
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="flex-shrink-0 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleDialogClose}
            className="bg-surface border border-surface-border hover:bg-surface-hover hover:border-surface-border text-white"
          >
            {isReadOnly ? "Close" : "Cancel"}
          </Button>
          {!isReadOnly && (
            <Button
              onClick={handleSubmit}
              className="bg-primary hover:bg-primary-dark text-white"
              disabled={
                label === "Api"
                  ? !apiFields.url.trim() || !apiFields.method.trim()
                  : !data.trim()
              }
            >
              {node ? "Update" : "Create"}
            </Button>
          )}
        </div>
      </DialogContent>

      {/* Image Preview Modal */}
      {previewImage && (
        <Dialog
          open={!!previewImage}
          onOpenChange={(open) => !open && handleClosePreview()}
        >
          <DialogContent className="sm:max-w-4xl bg-surface text-white border-surface-border max-h-[90vh] p-0" aria-describedby={undefined}>
            <DialogHeader className="p-4 pb-2">
              <DialogTitle className="text-lg font-semibold text-white">
                {previewImage.name}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Preview of the attached image.
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-hidden">
              <div className="w-full h-full max-h-[calc(90vh-80px)] flex items-center justify-center bg-black/20 p-4">
                {isLoadingSignedUrl && previewImage?.needsSignedUrl ? (
                  <div className="flex flex-col items-center justify-center text-white space-y-3">
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                    <span className="text-sm">Loading signed URL...</span>
                  </div>
                ) : displaySignedUrl ? (
                  <img
                    src={displaySignedUrl.replace(/^"+|"+$/g, "")}
                    alt={previewImage.name}
                    className="max-w-full max-h-full object-contain rounded"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3QgeD0iMyIgeT0iMyIgd2lkdGg9IjE4IiBoZWlnaHQ9IjE4IiByeD0iMiIgcnk9IjIiIHN0cm9rZT0iIzk5OTk5OSIgc3Ryb2tlLXdpZHRoPSIyIiBmaWxsPSJub25lIi8+CjxjaXJjbGUgY3g9Ijg1IiBjeT0iOCIgcj0iMiIgZmlsbD0iIzk5OTk5OSIvPgo8cGF0aCBkPSJtMjEgMTUtMy41LTMuNWE2IDYgMCAwIDAtOCAwTDMgMTgiIHN0cm9rZT0iIzk5OTk5OSIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+";
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center text-muted space-y-3">
                    <Image className="w-10 h-10 text-muted" />
                    <div className="text-center">
                      <p className="text-sm font-medium text-muted">
                        Image Not Available
                      </p>
                      <p className="text-xs text-muted mt-1">
                        Failed to load image preview
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Image metadata */}
            <div className="p-4 pt-2 border-t border-surface-border">
              <div className="flex items-center justify-between text-sm text-muted">
                <span>
                  Image {previewImage.index + 1} of {uploadedImages.length}
                </span>
                {uploadedImages[previewImage.index] &&
                  !(uploadedImages[previewImage.index] as any).isExisting && (
                    <span>
                      {Math.round(
                        uploadedImages[previewImage.index].size / 1024
                      )}{" "}
                      KB
                    </span>
                  )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

const API_TYPE_SUGGESTIONS = [
  "REST",
  "GraphQL",
  "gRPC",
  "WebSocket",
  "Event",
  "SOAP",
];

/**
 * Free-text input for API protocol type with a styled suggestion dropdown.
 * Native <datalist> wasn't reliably appearing in Chrome and looked off-theme,
 * so this is a small controlled combo: dropdown opens on focus, click-to-pick,
 * and the input still accepts any string (backend stores apis[].type as free
 * text — see api-graph.entity.ts).
 */
function ApiTypeCombobox({
  value,
  onChange,
  readOnly = false,
}: {
  value: string;
  onChange: (next: string) => void;
  readOnly?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close when the click lands outside the wrapper (input + dropdown).
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const query = value.trim().toLowerCase();
  const filtered = query
    ? API_TYPE_SUGGESTIONS.filter((s) => s.toLowerCase().includes(query))
    : API_TYPE_SUGGESTIONS;

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => !readOnly && setOpen(true)}
        placeholder="REST, GraphQL, gRPC, WebSocket, Event, SOAP…"
        className="bg-surface-active border-surface-border text-white placeholder:text-muted"
        readOnly={readOnly}
      />
      {open && !readOnly && filtered.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-md border border-surface-border bg-surface-active shadow-lg">
          {filtered.map((option) => (
            <li
              key={option}
              onMouseDown={(e) => {
                // mousedown so we fire before the input loses focus
                e.preventDefault();
                onChange(option);
                setOpen(false);
              }}
              className={`px-3 py-2 text-sm cursor-pointer hover:bg-surface-hover ${
                option === value ? "text-primary font-medium" : "text-white"
              }`}
            >
              {option}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
